import React, { memo } from 'react'
import DndDraggable from 'blocks.app/dnd/_draggable/dnd_draggable'
import DndDroppable from 'blocks.app/dnd/_droppable/dnd_droppable'
import { Icon } from 'molecules/Icon'
import helpers from 'core/helpers'
import translate from 'core/translate'
import { cn } from 'ethcss'
import { treeViewCatalogListStyles as styles } from './TreeViewCatalogList-styles'
import { mediaJS, indents } from 'theme'
import { api } from 'core/api/ConnectionManager'
import { ApiEventsType } from 'core/api/events'
import { getNestedFolderKey, TTreeViewTypeInstances } from 'core/helpers/treeView'
import { TActionType } from 'core/models/Roles'
import {
    IActionIcon,
    IRequiredActionsForActionMap,
    IActionToPermission,
    IActionToPermissionMap,
    ITreeViewCatalogListComponent,
    ITreeViewCatalogListContentComponent,
    IActionIconTooltip,
    ITreeViewCatalogEntity,
    ITreeViewCatalogListField,
    ITreeViewCatalogListItem,
    ITreeViewCatalogListTemplate,
    TreeViewCatalogItemId,
} from './TreeViewCatalogList-types'

const EXPAND_ICON_SIZE = 16

const isTreeViewCatalogEntity = (value: unknown): value is ITreeViewCatalogEntity => {
    if (typeof value !== 'object' || value === null) {
        return false
    }

    const { id } = value as { id?: unknown }

    return typeof id === 'string' || typeof id === 'number'
}

const PERMISSIONS_ICONS: IActionIcon[] = [
    {
        type: 'book',
        action: 'read',
        tooltip: 'reading',
    },
    {
        type: 'pencil',
        action: 'update',
        tooltip: 'updating',
    },
    {
        type: 'drag_document',
        action: 'replace',
        tooltip: 'replacing',
    },
    {
        type: 'trash_v2',
        action: 'delete',
        tooltip: 'deleting',
    },
]

const REQUIRED_ACTIONS_FOR_ACTION: IRequiredActionsForActionMap = {
    update: ['read'],
    delete: ['read', 'update'],
    replace: ['read', 'delete', 'update'],
}

const clonePermission = (permission: IActionToPermission): IActionToPermission => {
    return helpers.deepCopy(permission)
}

const getMarginLeftForCell = (isExpandElement: boolean, nestedLevel: number, isFolder: boolean) => {
    const initIndent = mediaJS.tabMini.matches ? 20 : 40
    let marginLeft = initIndent * nestedLevel

    if (isExpandElement && !isFolder) {
        marginLeft += indents.mini + EXPAND_ICON_SIZE
    }

    return { marginLeft }
}

const getFilterByPropertyObject = (type: TTreeViewTypeInstances): Record<string, TreeViewCatalogItemId[]> => {
    switch (type) {
        case 'displays':
        case 'digitalSignages':
            return {
                groupId: [],
                digitalSignageId: [],
            }
        case 'files':
            return {
                sourceId: [],
                sourceFolderId: [],
            }
        case 'broadcasts':
            return {
                broadcastId: [],
                broadcastFolderId: [],
            }
        default:
            return { id: [], folderId: [] }
    }
}

const getDefaultActionModel = (
    model: IActionToPermission,
    catalog: TTreeViewTypeInstances
): IActionToPermission & { filterByProperty: Record<string, TreeViewCatalogItemId[]> } => {
    return {
        ...model,
        active: true,
        filterByProperty: {
            ...getFilterByPropertyObject(catalog),
        },
    }
}

const getFilterByPropertyKey = (type: TTreeViewTypeInstances, isDirectory: boolean) => {
    switch (type) {
        case 'digitalSignages':
        case 'displays':
            if (isDirectory) {
                return 'groupId'
            }

            return 'digitalSignageId'
        case 'files':
            if (isDirectory) {
                return 'sourceFolderId'
            }

            return 'sourceId'
        case 'broadcasts':
            if (isDirectory) {
                return 'broadcastFolderId'
            }

            return 'broadcastId'
        default:
            if (isDirectory) {
                return 'folderId'
            }

            return 'id'
    }
}

const getDescendantIds = async (
    folderId: TreeViewCatalogItemId,
    list: ITreeViewCatalogListItem[],
    catalogType: TTreeViewTypeInstances,
    template: ITreeViewCatalogListTemplate
) => {
    const folderIds: TreeViewCatalogItemId[] = []
    const itemIds: TreeViewCatalogItemId[] = []

    const collectFromList = (parentId: TreeViewCatalogItemId) => {
        list.forEach((item) => {
            const nestedKey = item.__view?.nestedKey

            if (!nestedKey || item[nestedKey] !== parentId) {
                return
            }

            if (template.isFolder(item)) {
                folderIds.push(item.id)
                collectFromList(item.id)
            } else {
                itemIds.push(item.id)
            }
        })
    }

    collectFromList(folderId)

    if (folderIds.length > 0 || itemIds.length > 0) {
        return { folderIds, itemIds }
    }

    if (!template.getMethod) {
        return { folderIds, itemIds }
    }

    const folderKey = getNestedFolderKey(catalogType)
    const queue: TreeViewCatalogItemId[] = [folderId]

    while (queue.length > 0) {
        const parentId = queue.shift() as TreeViewCatalogItemId
        const requestOptions: Record<string, unknown> = { [folderKey]: parentId }

        if (catalogType === 'broadcasts') {
            requestOptions.sort = [
                ['type', 'ASC'],
                ['createdAt', 'DESC'],
                ['id', 'ASC'],
            ]
        }

        const { data } = (await api.send(template.getMethod as ApiEventsType, requestOptions, {
            hideLoader: true,
        })) as { data?: ITreeViewCatalogListItem[] }

        for (const item of (data || []) as ITreeViewCatalogListItem[]) {
            if (template.isFolder(item)) {
                folderIds.push(item.id)
                queue.push(item.id)
            } else {
                itemIds.push(item.id)
            }
        }
    }

    return { folderIds, itemIds }
}

const removeIdsFromPermission = (
    permission: IActionToPermission,
    catalogType: TTreeViewTypeInstances,
    folderIds: TreeViewCatalogItemId[],
    itemIds: TreeViewCatalogItemId[]
) => {
    if (!permission.filterByProperty) {
        return permission
    }

    const nextPermission = clonePermission(permission)
    const filterByProperty = nextPermission.filterByProperty

    if (!filterByProperty) {
        return nextPermission
    }

    const folderKey = getFilterByPropertyKey(catalogType, true)
    const itemKey = getFilterByPropertyKey(catalogType, false)

    if (filterByProperty[folderKey]) {
        filterByProperty[folderKey] = filterByProperty[folderKey].filter((id) => !folderIds.includes(id))
    }

    if (filterByProperty[itemKey]) {
        filterByProperty[itemKey] = filterByProperty[itemKey].filter((id) => !itemIds.includes(id))
    }

    return nextPermission
}

const TreeViewCatalogListPermissionsIcon = ({
    onClick,
    type,
    action,
    active,
    disabled,
    tooltip,
}: {
    onClick: (action: TActionType) => void
    type: string
    action: TActionType
    active: boolean
    disabled?: boolean
    tooltip: IActionIconTooltip
}) => {
    const handleOnClick = () => {
        onClick(action)
    }

    const getIconColor = () => {
        const icon = {
            color: active ? 'blue' : 'cloud',
            type,
        }

        if (disabled) {
            icon.color = 'blueLight'
            icon.type = `inherit_${type}`
        }

        return icon
    }

    const iconState = getIconColor()

    return (
        <Icon
            className={styles.permissionsIcon}
            type={iconState.type}
            onClick={handleOnClick}
            color={iconState.color}
            disabled={disabled}
            tooltip={tooltip}
        />
    )
}

const TreeViewCatalogListPermissionsIcons = ({
    onChangePermissions,
    permissions,
    listItem,
    catalogType,
    template,
    list,
}: {
    onChangePermissions: (permissions: IActionToPermissionMap) => void
    permissions: IActionToPermissionMap
    listItem: ITreeViewCatalogListItem
    catalogType: TTreeViewTypeInstances
    template: ITreeViewCatalogListTemplate
    list: ITreeViewCatalogListItem[]
}) => {
    const getStatus = (action: TActionType) => {
        const currentPermission = permissions[action]
        let isFolder = false

        if (!currentPermission || !currentPermission.filterByProperty) {
            return false
        }

        if (template.isFolder(listItem)) {
            isFolder = true
        }

        const array = currentPermission.filterByProperty[getFilterByPropertyKey(catalogType, isFolder)]

        return array && array.includes(listItem.id)
    }

    const handleOnClick = async (action: TActionType) => {
        let currentPermission = permissions[action]
        let isFolder = false
        let isSetRequired = false
        let isDeleteRequired = false

        if (!currentPermission.filterByProperty) {
            currentPermission = getDefaultActionModel(permissions[action], catalogType)
        } else {
            currentPermission = clonePermission(currentPermission)
        }

        if (template.isFolder(listItem)) {
            isFolder = true
        }

        const filterKey = getFilterByPropertyKey(catalogType, isFolder)
        const filterByProperty = currentPermission.filterByProperty

        if (!filterByProperty) {
            return
        }

        const array = filterByProperty[filterKey] || (filterByProperty[filterKey] = [])

        if (array.includes(listItem.id)) {
            array.splice(array.indexOf(listItem.id), 1)
            isDeleteRequired = true
        } else {
            array.push(listItem.id)
            isSetRequired = true
        }

        const required = REQUIRED_ACTIONS_FOR_ACTION[action]
        let newPermissions: IActionToPermissionMap = {}

        if (required && isSetRequired) {
            required.forEach((requiredAction: TActionType) => {
                let requiredPermission = permissions[requiredAction]

                if (requiredPermission) {
                    if (!requiredPermission.filterByProperty) {
                        requiredPermission = getDefaultActionModel(requiredPermission, catalogType)
                    }

                    let updatedPermission = clonePermission(requiredPermission)
                    const updatedFilterByProperty = updatedPermission.filterByProperty

                    if (!updatedFilterByProperty) {
                        return
                    }

                    const filterKey = getFilterByPropertyKey(catalogType, isFolder)
                    const array = updatedFilterByProperty[filterKey] || (updatedFilterByProperty[filterKey] = [])

                    if (!array.includes(listItem.id)) {
                        array.push(listItem.id)
                    }

                    newPermissions[requiredAction] = updatedPermission
                }
            })
        }

        let deletedPermissions: IActionToPermissionMap = {}
        const descendantIds =
            isDeleteRequired && isFolder ? await getDescendantIds(listItem.id, list, catalogType, template) : null
        const folderIdsToRemove = isFolder ? [listItem.id, ...(descendantIds?.folderIds || [])] : []
        const itemIdsToRemove = isFolder ? descendantIds?.itemIds || [] : [listItem.id]

        if (isDeleteRequired) {
            if (descendantIds) {
                currentPermission = removeIdsFromPermission(
                    currentPermission,
                    catalogType,
                    descendantIds.folderIds,
                    descendantIds.itemIds
                )
            }

            Object.entries(permissions).forEach(([permAction, permission]) => {
                let requiredActions = REQUIRED_ACTIONS_FOR_ACTION[permAction]

                if (requiredActions && requiredActions.includes(action)) {
                    if (!permission.filterByProperty) {
                        permission = getDefaultActionModel(permission, catalogType)
                    }

                    deletedPermissions[permAction] = removeIdsFromPermission(
                        permission,
                        catalogType,
                        folderIdsToRemove,
                        itemIdsToRemove
                    )
                }
            })
        }

        onChangePermissions({ ...newPermissions, [action]: currentPermission, ...deletedPermissions })
    }

    const getIconTooltipText = (disabled: boolean, icon: IActionIcon): IActionIconTooltip => {
        let tooltipText = icon.tooltip

        if (disabled) {
            if (catalogType === 'displays') {
                tooltipText = 'inherit_group_permissions'
            } else {
                tooltipText = 'inherit_folder_permissions'
            }
        }

        return {
            title: translate(tooltipText),
        }
    }

    return (
        <td>
            <div className={styles.permissionsIconsWrapper}>
                {PERMISSIONS_ICONS.map((icon) => {
                    if (catalogType !== 'displays' && icon.action === 'replace') {
                        return null
                    }

                    if (!Object.keys(permissions).includes(icon.action)) {
                        return null
                    }

                    const disabled = listItem.__view[`disabled_${icon.action}`] === true

                    return (
                        <TreeViewCatalogListPermissionsIcon
                            key={icon.type}
                            type={icon.type}
                            action={icon.action}
                            active={getStatus(icon.action)}
                            disabled={disabled}
                            onClick={handleOnClick}
                            tooltip={getIconTooltipText(disabled, icon)}
                        />
                    )
                })}
            </div>
        </td>
    )
}

const TreeViewCatalogListContent = (p_: ITreeViewCatalogListContentComponent) => {
    const dragData = p_.template.getDragData({ item: p_.listItemData.listItem })
    const onDrop = (data: { dnd_item: string }, e: React.DragEvent) => {
        e.stopPropagation()

        let droppedItem: ITreeViewCatalogEntity

        try {
            const parsedItem: unknown = JSON.parse(data.dnd_item)

            if (!isTreeViewCatalogEntity(parsedItem)) {
                return
            }

            droppedItem = parsedItem
        } catch (error) {
            return
        }

        const { listItem } = p_.listItemData

        if (p_.template.isEqual(droppedItem, listItem)) {
            return
        }
        const moveMethod = p_.template.isFolder(droppedItem) ? p_.template.moveFolder : p_.template.moveItem
        moveMethod(droppedItem.id, listItem.id)
    }
    const { listItem, options } = p_.listItemData
    const isFolder = p_.template.isFolder(listItem)

    return (
        <DndDroppable
            tag="tr"
            data={dragData}
            dragEnabled={p_.drag}
            dropEnabled={isFolder}
            wrapperComponent={{
                type: DndDraggable,
            }}
            onDragStart={p_.onDragStart}
            onDragEnd={p_.onDragEnd}
            onDrop={onDrop}
            className={styles.treeViewCatalogListRow}
        >
            {options.map((option, index) => (
                <td
                    key={`treeViewCatalogListItem_${listItem.id}_${index}`}
                    className={cn(styles.treeViewCatalogListTableCell)}
                    style={getMarginLeftForCell(!!option.expanded, p_.nestedLevel, isFolder)}
                >
                    {option.expanded && isFolder && (
                        <div className={styles.treeViewCatalogListTableCellIcon}>
                            <Icon
                                onClick={(e) => {
                                    e.stopPropagation()
                                    p_.onFolderOpening(listItem, p_.nested)
                                }}
                                size={EXPAND_ICON_SIZE}
                                type={listItem.__view.isOpen ? 'arrow_down' : 'arrow_right'}
                            />
                        </div>
                    )}
                    {p_.getCellValue(listItem, option.id)}
                </td>
            ))}
            {p_.permissions && (
                <TreeViewCatalogListPermissionsIcons
                    permissions={p_.permissions}
                    onChangePermissions={p_.onChangePermissions}
                    listItem={listItem}
                    catalogType={p_.type}
                    template={p_.template}
                    list={p_.list}
                />
            )}
        </DndDroppable>
    )
}

const TreeViewCatalogListComponent = (p_: ITreeViewCatalogListComponent) => {
    const getCellValue = (listItem: ITreeViewCatalogListItem, id: string): React.ReactNode => {
        const cellRender = p_.template.list

        if (cellRender && cellRender[id]) {
            return cellRender[id](listItem, p_)
        }

        const value = listItem[id]

        if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            return value
        }

        if (React.isValidElement(value as {} | null | undefined)) {
            return value as React.ReactElement
        }

        if (typeof value === 'object') {
            return null
        }

        return null
    }

    const renderTree = (items: ITreeViewCatalogListItem[], options: ITreeViewCatalogListField[]) => {
        const copiedItems = helpers.deepCopy(items)
        const folders = copiedItems.map((item, index) => {
            item.__view.index = index
            return item
        })
        const nested = folders.filter((folder, _index, array) => {
            const parent = array.find(
                (parentItem) => p_.template.isFolder(parentItem) && parentItem.id === folder[folder.__view.nestedKey]
            )

            if (!parent) {
                return true
            }

            const permissions = p_.permissions

            if (permissions) {
                Object.keys(permissions).forEach((actionKey) => {
                    const action = permissions[actionKey]

                    if (!action.filterByProperty) {
                        return
                    }

                    const array = action.filterByProperty[getFilterByPropertyKey(p_.type, p_.template.isFolder(parent))]

                    if (array && (array.includes(parent.id) || parent.__view[`disabled_${actionKey}`] === true)) {
                        folder.__view[`disabled_${actionKey}`] = true
                    }
                })
            }

            ;(parent.children = parent.children || []).push(folder)

            return false
        })

        const elements: React.ReactNode[] = []

        const loopOptions = (data: ITreeViewCatalogListItem[], nestedLevel: number) => {
            data.forEach((elem) => {
                const isFolder = p_.template.isFolder(elem) ? 'folder' : 'item'

                elements.push(
                    <TreeViewCatalogListContent
                        {...p_}
                        getCellValue={getCellValue}
                        key={`treeViewCatalogListRow_${elem.__view.index}`}
                        listItemData={{ listItem: elem, index: elem.__view.index || 0, isFolder, options }}
                        nestedLevel={nestedLevel}
                    />
                )

                if (elem.children && elem.__view.isOpen) {
                    const counter = nestedLevel + 1
                    loopOptions(elem.children, counter)
                }
            })
        }

        loopOptions(nested, 0)
        return elements
    }

    const getHeader = () => {
        let iconWidth = 0
        if (p_.permissions) {
            iconWidth = 20
        }

        const thWidths = (100 - iconWidth) / p_.template.fields().length
        const trStyles = { width: `${thWidths}%` }

        return (
            <thead>
                {p_.template.fields().map((field) => (
                    <tr style={trStyles} key={field.id}></tr>
                ))}
                {p_.permissions && <tr style={trStyles}></tr>}
            </thead>
        )
    }

    return (
        <table className={cn(styles.treeViewCatalogListTableWrapper, p_.template.tableWrapper)}>
            {getHeader()}
            <tbody className={styles.catalogListTableBody}>{renderTree(p_.list, p_.template.fields())}</tbody>
        </table>
    )
}

export const TreeViewCatalogList = memo(TreeViewCatalogListComponent)
